import { PHONE_COUNTRY_DEFS } from '@/utils/phoneCountryData';

const SUPPORTED = new Set(PHONE_COUNTRY_DEFS.map((c) => c.code));

/** Primary IANA timezone → ISO country (location signal; overrides en-US browser language). */
const IANA_TIMEZONE_COUNTRY: Record<string, string> = {
  // South Asia
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Asia/Karachi': 'PK',
  'Asia/Dhaka': 'BD',
  'Asia/Colombo': 'LK',
  'Asia/Kathmandu': 'NP',
  'Asia/Kabul': 'AF',
  'Asia/Thimphu': 'BT',
  'Indian/Maldives': 'MV',
  // Middle East & Gulf
  'Asia/Dubai': 'AE',
  'Asia/Muscat': 'OM',
  'Asia/Qatar': 'QA',
  'Asia/Kuwait': 'KW',
  'Asia/Bahrain': 'BH',
  'Asia/Riyadh': 'SA',
  'Asia/Amman': 'JO',
  'Asia/Beirut': 'LB',
  'Asia/Baghdad': 'IQ',
  'Asia/Tehran': 'IR',
  'Asia/Jerusalem': 'IL',
  'Asia/Gaza': 'PS',
  'Asia/Hebron': 'PS',
  'Asia/Damascus': 'SY',
  'Asia/Aden': 'YE',
  'Europe/Istanbul': 'TR',
  // Asia Pacific
  'Asia/Singapore': 'SG',
  'Asia/Tokyo': 'JP',
  'Asia/Seoul': 'KR',
  'Asia/Shanghai': 'CN',
  'Asia/Hong_Kong': 'HK',
  'Asia/Taipei': 'TW',
  'Asia/Bangkok': 'TH',
  'Asia/Jakarta': 'ID',
  'Asia/Manila': 'PH',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Yangon': 'MM',
  'Asia/Phnom_Penh': 'KH',
  'Asia/Vientiane': 'LA',
  'Asia/Brunei': 'BN',
  'Asia/Dili': 'TL',
  'Asia/Ulaanbaatar': 'MN',
  'Asia/Almaty': 'KZ',
  'Asia/Tashkent': 'UZ',
  'Asia/Tbilisi': 'GE',
  'Asia/Yerevan': 'AM',
  'Asia/Baku': 'AZ',
  // Russia & CIS
  'Europe/Moscow': 'RU',
  'Asia/Yekaterinburg': 'RU',
  'Asia/Novosibirsk': 'RU',
  'Asia/Vladivostok': 'RU',
  'Europe/Minsk': 'BY',
  'Europe/Kyiv': 'UA',
  'Europe/Kiev': 'UA',
  // Europe
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Copenhagen': 'DK',
  'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Bucharest': 'RO',
  'Europe/Athens': 'GR',
  'Europe/Lisbon': 'PT',
  'Europe/Belgrade': 'RS',
  'Europe/Sofia': 'BG',
  'Europe/Zagreb': 'HR',
  'Europe/Ljubljana': 'SI',
  'Europe/Bratislava': 'SK',
  'Europe/Tallinn': 'EE',
  'Europe/Riga': 'LV',
  'Europe/Vilnius': 'LT',
  'Europe/Luxembourg': 'LU',
  'Europe/Malta': 'MT',
  'Asia/Nicosia': 'CY',
  'Atlantic/Reykjavik': 'IS',
  // Americas
  'America/New_York': 'US',
  'America/Detroit': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Mexico_City': 'MX',
  'America/Sao_Paulo': 'BR',
  // Oceania & Africa
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Perth': 'AU',
  'Pacific/Auckland': 'NZ',
  'Africa/Johannesburg': 'ZA',
};

export const DEFAULT_PHONE_COUNTRY_CODE = 'IN';

function isSupportedCountry(code: string | undefined | null): code is string {
  return !!code && SUPPORTED.has(code.toUpperCase());
}

/** Infer ISO country from the browser IANA timezone (e.g. Asia/Kolkata → IN). */
export function detectCountryFromTimezone(): string | null {
  if (typeof Intl === 'undefined') return null;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    if (!tz) return null;
    const mapped = IANA_TIMEZONE_COUNTRY[tz];
    if (isSupportedCountry(mapped)) return mapped.toUpperCase();
  } catch {
    // unsupported environment
  }
  return null;
}

/** Infer ISO country from browser locale tags with an explicit region (e.g. en-IN → IN). */
export function detectCountryFromLocales(): string | null {
  try {
    const locales =
      typeof navigator !== 'undefined'
        ? [navigator.language, ...(navigator.languages ?? [])]
        : [];
    for (const locale of locales) {
      if (!locale?.trim()) continue;
      const match = locale.trim().match(/[-_]([A-Za-z]{2})(?:[-_]|$)/);
      if (match && isSupportedCountry(match[1])) return match[1].toUpperCase();
    }
  } catch {
    // unsupported environment
  }
  return null;
}

/**
 * Best-effort phone country for unsigned flows (register, etc.):
 * browser timezone → browser locale → India.
 */
export function detectBrowserCountryCode(): string {
  return detectCountryFromTimezone() ?? detectCountryFromLocales() ?? DEFAULT_PHONE_COUNTRY_CODE;
}

/** Legacy / Windows link IDs → canonical zones seeded in platform master data. */
const BROWSER_TIMEZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Arizona': 'America/Phoenix',
  'Canada/Eastern': 'America/Toronto',
  'Canada/Pacific': 'America/Vancouver',
  'Canada/Central': 'America/Winnipeg',
  'Europe/Belfast': 'Europe/London',
  GB: 'Europe/London',
  'GB-Eire': 'Europe/London',
  'Etc/UTC': 'UTC',
  'Etc/GMT': 'UTC',
  GMT: 'UTC',
  Zulu: 'UTC',
  UCT: 'UTC',
};

/** Browser IANA timezone when available (for org defaults during signup). */
export function detectBrowserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    if (tz) return BROWSER_TIMEZONE_ALIASES[tz] ?? tz;
  } catch {
    // fall through
  }
  return 'Asia/Kolkata';
}
